import { useCallback, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { rollBack } from '|/app/services';
import { ActiveStepContext, CheckItOutContext, FormStateContext } from '|/context';
import { useRefreshDashboardSummary } from '|/hooks/use-dashboard-summary-refresh';

export interface SubmitOptions {
  /**
   * If true, will refresh dashboard and summary after successful submission
   */
  refreshOnComplete?: boolean;
}

const useSubmit = () => {
  const { startSubmit, endSubmit, setSubmitError } = useContext(FormStateContext);
  const refreshDashboardSummary = useRefreshDashboardSummary();
  const { isCheckItOutTreatment } = useContext(CheckItOutContext);
  const { activeStepUrl } = useContext(ActiveStepContext);
  const { pathname } = useLocation();

  const isOnActiveStep =
    activeStepUrl === undefined || pathname.startsWith(`/steps/${activeStepUrl}`);

  const submit = useCallback(
    async (
      promiseOrFactory: Promise<void> | (() => Promise<void>),
      signal?: AbortSignal,
      options?: SubmitOptions
    ): Promise<void> => {
      startSubmit();
      try {
        if (isCheckItOutTreatment && !isOnActiveStep) {
          await rollBack({ cancelTransfer: true, keepReviewOrder: false });
        }

        const promise =
          typeof promiseOrFactory === 'function' ? promiseOrFactory() : promiseOrFactory;
        await promise;

        if (options?.refreshOnComplete) {
          refreshDashboardSummary();
        }
      } catch (errorFromSubmit) {
        if (signal?.aborted) {
          return;
        }
        setSubmitError(errorFromSubmit);
        reportError(errorFromSubmit);
        throw errorFromSubmit;
      } finally {
        endSubmit();
      }
    },
    [
      startSubmit,
      endSubmit,
      setSubmitError,
      refreshDashboardSummary,
      isCheckItOutTreatment,
      isOnActiveStep
    ]
  );

  return submit;
};

export default useSubmit;
